import { HeaderPositionType } from "../constants";
import { chartUtils } from "../slideTemplates/latest";
import { BaseCategory } from "./BaseCategory";
import { SlideModelBuilder } from "./SlideModelBuilder";

const { getDefaultWaterfallChartModel } = chartUtils;
const WATERFALL_PROMPT_PATTERN = /\b((waterfall|bridge)\s*chart|running total|cumulative effect|net income bridge|revenue bridge|profit bridge)\b/i;

export class ChartCategory extends BaseCategory {
    get name() {
        return "Chart";
    }

    get type() {
        return "chart";
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for visualizing data trends, comparisons, or patterns with line charts, bar charts, column charts, area charts, or waterfall charts. Also use for bridge charts, running-total charts, and cumulative add/subtract views that show how an initial value changes through positive and negative steps to a final total. Don't use for pie charts.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a chart slide based on the user's prompt.
        Provide the chart data including categories (x-axis labels) and one or more data series.
        Specify the chart type: line, column, bar, area, or waterfall.`;
    }

    getPromptSchema() {
        return {
            chartType: "type of chart: line, column, bar, area, or waterfall",
            categories: ["array of x-axis category labels (e.g., months, years, product names)"],
            series: [
                {
                    name: "name of the data series",
                    data: ["array of numeric values corresponding to each category"]
                }
            ],
            xAxisTitle: "optional title for the x-axis",
            yAxisTitle: "optional title for the y-axis",
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.series) || !this.validateArray(model.categories)) {
            return false;
        }
        return true;
    }

    getImagePrompt() {
        return false;
    }

    getSlideModels(model, options = {}) {
        const chartType = this.resolveChartType(model);

        let models = [
            // Build the requested chart type first
            this.createChart(model, chartType, options),
        ];

        // Add variations for other chart types
        const chartTypes = ["line", "column", "bar", "area", "pie", "waterfall"];
        for (const type of chartTypes) {
            if (type !== chartType) {
                models.push(this.createChart(model, type, options));
            }
        }

        return models.filter(m => m != null);
    }

    resolveChartType(model = {}) {
        const chartType = `${model.chartType || ""}`.trim().toLowerCase();
        if (chartType) {
            return chartType;
        }

        return this.shouldPrioritizeWaterfall(model) ? "waterfall" : "line";
    }

    shouldPrioritizeWaterfall(model = {}) {
        return [model.slideTitle, model.userPrompt, model.prompt]
            .filter(value => typeof value === "string" && value.trim().length > 0)
            .some(value => WATERFALL_PROMPT_PATTERN.test(value));
    }

    createChart(model, chartType, options = {}) {
        if (chartType == "pie") {
            return this.buildPieChart(model, options);
        } else if (chartType == "waterfall") {
            return this.buildWaterfallChart(model, options);
        } else {
            return this.buildChart(model, chartType, options);
        }
    }

    buildChart(model, chartType, options = {}) {
        let builder = new SlideModelBuilder("dashboard", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });
        const categories = model.categories || ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
        const series = model.series || [
            {
                name: "Data",
                data: [10, 40, 50, 30, 70, 50]
            }
        ];

        // Build series data with proper structure
        const chartSeries = series.map((s, index) => {
            const seriesIndex = index + 1;
            return {
                id: `series${seriesIndex}`,
                name: s.name || `Series ${seriesIndex}`,
                type: chartType,
                data: s.data.map(y => ({ y: parseFloat(y), pt: true })),
                colorName: `chart${seriesIndex}`,
                marker: "none",
                zones: [
                    {
                        style: "default",
                    }
                ]
            };
        });

        const chartData = {
            series: chartSeries,
            xAxis: {
                visible: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                categories: categories,
                dateFormatting: "none",
                showAxisLine: true,
                showGridLine: false,
                showMajorTicks: false,
                showMinorTicks: false,
                axisTitle: !!model.xAxisTitle,
                axisTitleText: model.xAxisTitle || "Title",
                labelInterval: 1,
                type: "linear",
                autoType: "linear",
                categoryType: "auto",
                labels: {
                    enabled: true,
                },
                zeroAxisPadding: false
            },
            yAxis: {
                visible: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                showAxisLine: true,
                showGridLines: true,
                showMajorTicks: false,
                showMinorTicks: false,
                labelAbbreviation: false,
                labelCurrency: "none",
                labelFormat: "ones",
                axisTitle: model.yAxisTitle ? "edge" : "none",
                axisTitleText: model.yAxisTitle || "Value",
                labels: {
                    enabled: true
                },
                format: "number",
                formatOptions: {
                    decimal: 0,
                    separator: true,
                    abbreviated: false,
                    changeStyle: "none",
                    changeColor: true,
                    currency: "$",
                    dateFormat: "MMM Do",
                    accountingStyle: false
                }
            },
            yAxis2: {
                visible: false,
                opposite: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                showAxisLine: true,
                showGridLines: true,
                showMajorTicks: false,
                showMinorTicks: false,
                labelAbbreviation: false,
                labelCurrency: "none",
                labelFormat: "ones",
                axisTitle: "none",
                axisTitleText: "Title",
                labels: {
                    enabled: true
                },
                format: "number",
                formatOptions: {
                    decimal: 0,
                    separator: true,
                    abbreviated: false,
                    changeStyle: "none",
                    changeColor: true,
                    currency: "$",
                    dateFormat: "MMM Do",
                    accountingStyle: false
                }
            },
            chartAnnotations: {
                items: [],
                connections: {
                    items: []
                }
            }
        };

        builder.createPrimary({
            fullBleed: false,
            showGutter: true,
            cellColor: "none",
            gridLayout: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 12, height: 12
                }]
            },
            items: [{
                componentType: "Chart",
                cellColor: "none",
                legendPosition: series.length > 1 ? "bottom" : "off",
                chartData: chartData
            }]
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildPieChart(model, options = {}) {
        if (model.series.length > 1 || (model.series[0] && model.series[0].data.length > 8)) {
            // Pie charts are not suitable for multiple series or too many items
            return null;
        }
        let builder = new SlideModelBuilder("dashboard", model);

        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.LEFT });

        builder.createPrimary({
            items: [{
                componentType: "PieChart",
                childElement: {
                    collectionColor: "theme-to-dark",
                    data: model.categories.map((category, index) => ({
                        value: model.series[0].data[index],
                        label: category
                    })),
                    format: "percent",
                    innerRadius: 0,
                    isDonut: false,
                    labelFormat: "percentage",
                    sortValues: true
                }
            }]
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    buildWaterfallChart(model, options = {}) {
        const builder = new SlideModelBuilder("chart_waterfall", model, true);
        const chartModel = getDefaultWaterfallChartModel();
        const { categories, seriesName, data } = this.normalizeWaterfallData(model);

        chartModel.chartData.xAxis.categories = categories;
        chartModel.chartData.xAxis.axisTitle = !!model.xAxisTitle;
        chartModel.chartData.xAxis.axisTitleText = model.xAxisTitle || "Title";
        chartModel.chartData.yAxis.axisTitle = model.yAxisTitle ? "edge" : "none";
        chartModel.chartData.yAxis.axisTitleText = model.yAxisTitle || "Value";
        chartModel.chartData.series = [{
            id: "series1",
            name: seriesName,
            type: "waterfall",
            data,
            colorName: "chart1",
            greenRed: false,
            showDataLabels: true
        }];

        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });
        builder.createPrimary({
            items: [{
                componentType: "Chart",
                childElement: chartModel
            }]
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    normalizeWaterfallData(model = {}) {
        const categories = Array.isArray(model.categories) && model.categories.length > 0
            ? model.categories
            : [
                "Beginning Value",
                "Positive Change",
                "Negative Change",
                "Ending Value"
            ];

        const rawSeries = Array.isArray(model.series) && model.series.length > 0
            ? model.series[0]
            : {
                name: "Data",
                data: [120, 45, -30, "sum"]
            };

        const data = (Array.isArray(rawSeries.data) ? rawSeries.data : []).map(value => {
            if (typeof value === "string" && ["sum", "total"].includes(value.trim().toLowerCase())) {
                return { y: "sum" };
            }

            const numericValue = parseFloat(value);
            return { y: Number.isFinite(numericValue) ? numericValue : 0 };
        });

        return {
            categories,
            seriesName: rawSeries.name || "Data",
            data: data.length > 0 ? data : [{ y: 120 }, { y: 45 }, { y: -30 }, { y: "sum" }]
        };
    }
}
